import { useEffect, useState } from 'react';
import { array } from 'fp-ts';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { getWallClockDateUtcString } from '@mews-commander/utils';
import { useRateIntersection, useCreateVoucher } from '../../../../queries';
import { FormErrors, WithEnterpriseIds } from '../../../../types';
import { FieldName, FieldsConfig, FormValues } from './types';
import { VoucherContainer } from './VoucherContainer';

interface CreateVoucherContainerProps extends WithEnterpriseIds {
    isOpened: boolean;
    onConfirm: () => Promise<void>;
    onCancel: () => void;
    rateKeys?: string[];
}

export const CreateVoucherContainer = ({
    enterpriseIds,
    isOpened,
    onConfirm,
    onCancel,
    rateKeys,
}: CreateVoucherContainerProps) => {
    const [formErrors, setFormErrors] = useState<FormErrors>();

    const createVoucher = useCreateVoucher();

    const initialFormValues: FormValues = {
        name: '',
        key: '',
        occupiableIntervalEndUtc: '',
        occupiableIntervalStartUtc: '',
        companyKey: '',
        properties: enterpriseIds,
        rateKeys: rateKeys || [],
    };

    useEffect(() => {
        if (!isOpened) {
            setFormErrors(undefined);
        }
    }, [isOpened]);

    const { data: intersectedRateKeysResult } = useRateIntersection({
        enterpriseIds,
        enabled: isOpened,
    });

    const isAssignedRatesFieldDisabled =
        intersectedRateKeysResult?.keys == null || array.isEmpty(intersectedRateKeysResult?.keys);
    const assignedRatesDisabledFields: FieldName[] = isAssignedRatesFieldDisabled ? ['rateKeys'] : [];

    const fieldsConfig: FieldsConfig = {
        disabledFields: [...assignedRatesDisabledFields],
    };

    const handleOnSubmit = ({
        properties,
        occupiableIntervalEndUtc,
        occupiableIntervalStartUtc,
        ...rest
    }: FormValues) => {
        createVoucher.mutate(
            {
                occupiableIntervalStart: getWallClockDateUtcString(occupiableIntervalStartUtc) || null,
                occupiableIntervalEnd: getWallClockDateUtcString(occupiableIntervalEndUtc) || null,
                enterpriseIds: properties || [],
                ...rest,
            },
            {
                onSuccess: onConfirm,
                onError: (e) => {
                    if (e instanceof Error && e.message.includes('CompanyKey')) {
                        setFormErrors({ field: 'companyKey', message: e.message });
                    }

                    if (e instanceof Error && e.message.includes('voucher key')) {
                        setFormErrors({ field: 'key', message: e.message });
                    }
                },
            }
        );
    };

    return (
        <VoucherContainer
            values={initialFormValues}
            active={isOpened}
            onCancel={onCancel}
            title={
                <LocalizedText
                    textKey={TextKey.CreateVouchersInBulk}
                    substitutes={{ propertiesCount: enterpriseIds.length }}
                />
            }
            enterpriseIds={enterpriseIds}
            fieldsConfig={fieldsConfig}
            formErrors={formErrors}
            submitButtonTextKey={TextKey.Create}
            propertyCount={enterpriseIds.length}
            onSubmit={handleOnSubmit}
            isLoading={createVoucher.isPending}
        />
    );
};
